import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';

/**
 * Navigation Component
 * 
 * Main navigation bar with logo, menu items, and language switcher.
 * Features:
 * - Responsive mobile hamburger menu
 * - Active section highlighting using Intersection Observer
 * - Smooth scroll to sections with offset for fixed header
 * - Glassmorphism design with backdrop blur
 * - Language switcher integration
 * 
 * The navigation automatically highlights the current section as the user scrolls
 * through the page and provides smooth scrolling navigation to different sections.
 * 
 * @component
 * @example
 * ```tsx
 * <Navigation />
 * ```
 */
export default function Navigation() {
  const { t } = useTranslation('common');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');

  const menuItems = [
    { label: t('navigation.home'), href: '/' },
    { label: t('navigation.about'), href: '#about' },
    { label: t('navigation.services'), href: '#services' },
    { label: t('navigation.contact'), href: '#contact' },
  ];

  // Intersection Observer for active section highlighting
  useEffect(() => {
    const sections = document.querySelectorAll('section[id]');
    
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px', // Trigger when section is near top of viewport
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(`#${entry.target.id}`);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  const isActive = (href: string) => {
    if (href === '/') {
      return window.location.pathname === '/' && !activeSection;
    }
    return href === activeSection;
  };

  return (
    <nav className="bg-white/95 backdrop-blur-sm shadow-md sticky top-0 z-50 border-b border-primary-100">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <a href="/" className="text-2xl font-display font-bold bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent hover:from-primary-600 hover:to-accent-600 transition-all">
              Suersdey
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-8">
            {menuItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={`font-medium transition-all relative ${
                  isActive(item.href)
                    ? 'text-primary-600 font-semibold after:absolute after:bottom-[-8px] after:left-0 after:right-0 after:h-0.5 after:bg-gradient-to-r after:from-primary-500 after:to-accent-500'
                    : 'text-neutral-700 hover:text-primary-600'
                }`}
              >
                {item.label}
              </a>
            ))}
            <LanguageSwitcher />
          </div>

          {/* Mobile menu button and language switcher */}
          <div className="flex md:hidden items-center gap-3">
            <LanguageSwitcher />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-neutral-700 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md p-2"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-neutral-200">
            <div className="flex flex-col space-y-4">
              {menuItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className={`font-medium transition-colors px-2 ${
                    isActive(item.href)
                      ? 'text-primary-600 font-semibold'
                      : 'text-neutral-700 hover:text-primary-600'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
